/**
 * Notifications — directApiClient (LF-3214)
 *
 * `revalidatePath` calls dropped — invalidation now happens via React Query
 * at the hook layer (see hooks.tsx + query-dependencies.ts `notification`).
 */

'use client';

import { directApiClient } from '@/lib/api/direct-client';
import {
  Notification,
  NotificationsListResponse,
  UnreadCountResponse,
  MattersWithUnreadResponse,
  NotificationActionResult
} from './types';

export async function getNotificationsAction(): Promise<
  NotificationActionResult<NotificationsListResponse>
> {
  try {
    const response = await directApiClient.get<{
      items?: Notification[];
      meta?: { totalItems?: number };
    }>('/notifications');

    const notifications = response.items || [];
    const total = response.meta?.totalItems || notifications.length;
    return { success: true, data: { notifications, total } };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'Failed to fetch notifications'
    };
  }
}

export async function getUnreadCountAction(): Promise<
  NotificationActionResult<UnreadCountResponse>
> {
  try {
    const response = await directApiClient.get<UnreadCountResponse>(
      '/notifications/unread-count'
    );
    return { success: true, data: response };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'Failed to fetch unread count'
    };
  }
}

export async function getMattersWithUnreadAction(): Promise<
  NotificationActionResult<MattersWithUnreadResponse>
> {
  try {
    const response = await directApiClient.get<
      MattersWithUnreadResponse | MattersWithUnreadResponse['matters']
    >('/notifications/matters/with-unread');

    let result: MattersWithUnreadResponse;
    if (Array.isArray(response)) {
      const totalUnread = response.reduce(
        (sum, matter) => sum + (matter.unreadCount || 0),
        0
      );
      result = { matters: response, totalUnread };
    } else {
      result = response;
    }

    return { success: true, data: result };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to fetch matters with unread'
    };
  }
}

export async function markNotificationReadAction(
  notificationId: number
): Promise<NotificationActionResult<Notification>> {
  try {
    const updated = await directApiClient.patch<Notification>(
      `/notifications/${notificationId}/read`
    );
    return { success: true, data: updated };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to mark notification as read'
    };
  }
}

export async function markAllNotificationsReadAction(): Promise<
  NotificationActionResult<void>
> {
  try {
    await directApiClient.patch('/notifications/read-all');
    return { success: true };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to mark all notifications as read'
    };
  }
}
